import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Select from "react-select";
import countries from "../../components/country";
import toast from "react-hot-toast";
import { getErrorMessage } from "../../utils/errorMessage";

function normalizeFields(fields = []) {
  // Ensure:
  // - phone is always first
  // - options are always string[]
  // - type default: "input"
  const fixed = Array.isArray(fields) ? fields : [];
  const normalized = fixed.map((f) => {
    const type = f?.type || "input";
    const options = Array.isArray(f?.options)
      ? f.options.map((opt) => (typeof opt === "string" ? opt : opt?.label || "")).filter(Boolean)
      : [];
    return {
      ...f,
      type,
      options,
      required: !!f?.required,
      label: f?.label || (f?.field === "phone" ? "Phone Number" : "Untitled Field"),
    };
  });

  // Lock phone required
  const phoneIndex = normalized.findIndex((x) => x.field === "phone");
  if (phoneIndex === -1) {
    normalized.unshift({ field: "phone", label: "Phone Number", required: true, type: "input", options: [] });
  } else {
    normalized[phoneIndex] = { ...normalized[phoneIndex], required: true, type: "input" };
    // move phone to top
    const [phone] = normalized.splice(phoneIndex, 1);
    normalized.unshift(phone);
  }

  return normalized;
}

function sanitizeUrl(input) {
  if (!input) return "";
  const trimmed = input.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function EditLeadFormModal({ show, onHide, onUpdate, formData }) {
  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formTheme, setFormTheme] = useState("#000000");
  const [customUrl, setCustomUrl] = useState("");

  // image can be:
  // - string URL (existing)
  // - File object (new)
  // - null (removed)
  const [formImage, setFormImage] = useState(null);

  const [enabledThankYouPage, setEnabledThankYouPage] = useState(false);
  const [thankYouPageMessage, setThankYouPageMessage] = useState("");

  const [enabledRedirect, setEnabledRedirect] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [isAutoRedirect, setIsAutoRedirect] = useState(false);

  const [enablePixel, setEnablePixel] = useState(false);
  const [faceBookPixel, setFaceBookPixel] = useState("");
  const [tikTokPixel, setTikTokPixel] = useState("");
  const [googlePixel, setGooglePixel] = useState("");

  const [selectedFields, setSelectedFields] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const countryOptions = useMemo(
    () =>
      countries.map((country) => ({
        value: country.dialCode,
        label: (
          <div style={{ display: "flex", alignItems: "center", fontSize: "0.8rem" }}>
            <img src={country.flag} alt={country.name} style={{ width: 20, marginRight: 6 }} />
            {country.isoCode} (+{country.dialCode})
          </div>
        ),
        name: country.name,
        isoCode: country.isoCode,
      })),
    []
  );

  // Load data when modal opens / form changes
  useEffect(() => {
    if (!formData) return;

    setFormTitle(formData.formTitle || "");
    setFormDescription(formData.formDescription || "");
    setFormTheme(formData.formTheme || "#000000");
    setCustomUrl(formData.customUrl || "");

    // IMPORTANT: keep existing URL as string
    setFormImage(formData.formImage || null);

    setSelectedFields(normalizeFields(formData.formConfig || []));

    setEnabledThankYouPage(!!formData.thankYouEnabled || !!formData.enabledThankYouPage);
    setThankYouPageMessage(formData.thankYouPageMessage || "");

    setEnabledRedirect(!!formData.redirectEnabled || !!formData.enabledRedirect);
    setRedirectUrl(formData.redirectUrl || "");
    setIsAutoRedirect(!!formData.autoRedirect || !!formData.isAutoRedirect);

    const hasPixel = !!(formData.pixelMeta || formData.pixelTiktok || formData.pixelGoogleAds);
    setEnablePixel(hasPixel);
    setFaceBookPixel(formData.pixelMeta || "");
    setTikTokPixel(formData.pixelTiktok || "");
    setGooglePixel(formData.pixelGoogleAds || "");
  }, [formData]);

  // If pixel disabled => clear fields
  useEffect(() => {
    if (!enablePixel) {
      setFaceBookPixel("");
      setTikTokPixel("");
      setGooglePixel("");
    }
  }, [enablePixel]);

  const moveUp = (index) => {
    if (index <= 1) return; // 0 is phone locked, 1 is next item
    const updated = [...selectedFields];
    [updated[index], updated[index - 1]] = [updated[index - 1], updated[index]];
    setSelectedFields(updated);
  };

  const moveDown = (index) => {
    const updated = [...selectedFields];
    if (index >= updated.length - 1) return;
    if (index === 0) return; // phone locked
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setSelectedFields(updated);
  };

  const handleDeleteField = (index) => {
    const field = selectedFields[index];
    if (field?.field === "phone") return;
    const updated = [...selectedFields];
    updated.splice(index, 1);
    setSelectedFields(updated);
  };

  const updateField = (index, key, value) => {
    const updated = [...selectedFields];
    updated[index] = { ...updated[index], [key]: value };
    setSelectedFields(updated);
  };

  const handleAddExtra = () => {
    const count = selectedFields.filter((f) => f.field === "extra").length + 1;
    setSelectedFields((prev) => [
      ...prev,
      { field: "extra", label: `Extra Option ${count}`, required: false, type: "input", options: [] },
    ]);
  };

  const handleAddName = () => {
    const alreadyHasName = selectedFields.some((f) => f.field === "name");
    if (alreadyHasName) return toast.error("Name field already added");
    setSelectedFields((prev) => [...prev, { field: "name", label: "Name", required: false, type: "input", options: [] }]);
  };

  const handleImagePick = (file) => {
    if (!file) return;

    // size check 4MB (align with backend msg)
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image too large. Max size is 4MB.");
      return;
    }
    // type check
    const ok = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
    if (!ok) {
      toast.error("Invalid file type. Only JPG, PNG, and WebP are allowed.");
      return;
    }
    setFormImage(file);
  };

  const getPreviewUrl = () => {
    if (!formImage) return null;
    if (typeof formImage === "string") return formImage;
    return URL.createObjectURL(formImage);
  };

  const handleSubmit = async () => {
    if (!formData?.id) return toast.error("Missing form id");
    if (!formTitle || !customUrl || !selectedFields.length) return toast.error("Please complete all fields.");

    setSubmitting(true);
    try {
      // IMPORTANT:
      // only send formImage when it's File or null (remove)
      // if it's string => don't include, means keep existing
      const payload = {
        formTitle,
        formDescription,
        formTheme,
        customUrl,
        selectedFields,
        thankYouEnabled: enabledThankYouPage,
        thankYouPageMessage,
        redirectEnabled: enabledRedirect,
        redirectUrl,
        autoRedirect: isAutoRedirect,
        pixelMeta: enablePixel ? faceBookPixel : "",
        pixelTiktok: enablePixel ? tikTokPixel : "",
        pixelGoogleAds: enablePixel ? googlePixel : "",
      };

      // Remove image
      if (formImage === null) payload.removeImage = true;

      // New file
      if (formImage && typeof formImage !== "string") {
        payload.formImageFile = formImage;
      }

      await onUpdate(formData.id, payload);
      toast.success("Form updated!");
      onHide();
    } catch (err) {
      toast.error(getErrorMessage(err, "Error updating form"));
    } finally {
      setSubmitting(false);
    }
  };

  const easyBorangBase = process.env.REACT_APP_EASYBORANG || "";
  const previewLink = `${easyBorangBase}${customUrl || ""}`;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered backdrop="static" className="ss-page">
      <Modal.Header closeButton className="ss-modal-header">
        <Modal.Title>Edit Form</Modal.Title>
      </Modal.Header>

      <Modal.Body className="ss-modal-body">
        <div className="row g-3">
          {/* LEFT */}
          <div className="col-lg-6">
            <div className="ds-card p-3">
              <div className="form-floating mb-3">
                <input type="text" className="form-control" placeholder="Custom URL" value={customUrl} disabled />
                <label>Custom URL (locked)</label>
              </div>

              <div className="form-floating mb-3">
                <input type="text" className="form-control" placeholder="Form Title" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} />
                <label>Form Title</label>
              </div>

              <div className="form-floating mb-3">
                <textarea className="form-control" placeholder="Form Description" style={{ height: 110 }} value={formDescription} onChange={(e) => setFormDescription(e.target.value)} />
                <label>Form Description</label>
              </div>

              <div className="mb-3">
                <label className="form-label">Form Image</label>
                <input type="file" className="form-control" onChange={(e) => handleImagePick(e.target.files?.[0] || null)} />
                <div className="form-text">JPG / PNG / WebP, max 4MB</div>
              </div>

              <div className="mb-0">
                <label className="form-label">Theme Color</label>
                <input type="color" className="form-control form-control-color" value={formTheme} onChange={(e) => setFormTheme(e.target.value)} />
              </div>
            </div>

            <div className="ds-card p-3 mt-3">
              <div className="d-flex align-items-center justify-content-between">
                <h6 className="mb-0" style={{ fontWeight: 800, opacity: 0.85 }}>Form Fields</h6>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-dark btn-sm" onClick={handleAddExtra}>+ Extra</button>
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddName}>+ Name</button>
                </div>
              </div>

              <div className="mt-3">
                {selectedFields.map((field, index) => {
                  const isPhone = field.field === "phone";
                  const title = field.label || "Untitled Field";

                  return (
                    <div key={index} className="border rounded-4 p-3 mb-3">
                      <div className="d-flex align-items-start gap-2">
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="fw-bold text-truncate">{title}</div>
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {isPhone ? "Locked field" : `Type: ${field.type || "input"}`}
                          </div>
                        </div>

                        <div className="btn-group btn-group-sm">
                          <button type="button" className="btn btn-outline-secondary" onClick={() => moveUp(index)} disabled={index <= 1}>
                            <i className="bi bi-arrow-up"></i>
                          </button>
                          <button type="button" className="btn btn-outline-secondary" onClick={() => moveDown(index)} disabled={index === 0 || index >= selectedFields.length - 1}>
                            <i className="bi bi-arrow-down"></i>
                          </button>
                          <button type="button" className="btn btn-outline-danger" onClick={() => handleDeleteField(index)} disabled={isPhone}>
                            <i className="bi bi-x"></i>
                          </button>
                        </div>
                      </div>

                      <div className="form-floating mt-3">
                        <input type="text" className="form-control" placeholder="Label" value={field.label} onChange={(e) => updateField(index, "label", e.target.value)} />
                        <label>Custom Label</label>
                      </div>

                      {!isPhone && field.field !== "name" && (
                        <div className="form-floating mt-3">
                          <select className="form-select" value={field.type || "input"} onChange={(e) => updateField(index, "type", e.target.value)}>
                            <option value="input">Input Field</option>
                            <option value="select">Dropdown</option>
                          </select>
                          <label>Field Type</label>
                        </div>
                      )}

                      {!isPhone && field.type === "select" && (
                        <div className="mt-3">
                          <div className="text-muted mb-2" style={{ fontSize: 12, fontWeight: 700 }}>Dropdown Options</div>

                          {(field.options || []).map((opt, i) => (
                            <div key={i} className="d-flex align-items-center gap-2 mb-2">
                              <input
                                type="text"
                                className="form-control"
                                value={opt}
                                onChange={(e) => {
                                  const updated = [...selectedFields];
                                  const opts = Array.isArray(updated[index].options) ? [...updated[index].options] : [];
                                  opts[i] = e.target.value;
                                  updated[index].options = opts;
                                  setSelectedFields(updated);
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => {
                                  const updated = [...selectedFields];
                                  const opts = Array.isArray(updated[index].options) ? [...updated[index].options] : [];
                                  opts.splice(i, 1);
                                  updated[index].options = opts;
                                  setSelectedFields(updated);
                                }}
                              >
                                <i className="bi bi-x"></i>
                              </button>
                            </div>
                          ))}

                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => {
                              const updated = [...selectedFields];
                              const opts = Array.isArray(updated[index].options) ? [...updated[index].options] : [];
                              opts.push(`Option ${opts.length + 1}`);
                              updated[index].options = opts;
                              setSelectedFields(updated);
                            }}
                          >
                            + Add Option
                          </button>
                        </div>
                      )}

                      <div className="form-check mt-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`required-${index}`}
                          checked={!!field.required}
                          onChange={(e) => updateField(index, "required", e.target.checked)}
                          disabled={isPhone}
                        />
                        <label className="form-check-label" htmlFor={`required-${index}`}>
                          Required {isPhone && "(locked)"}
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="ds-card p-3 mt-3">
              <h6 className="mb-3" style={{ fontWeight: 800, opacity: 0.85 }}>After Submission</h6>

              <div className="form-check mb-2">
                <input className="form-check-input" type="checkbox" id="enableThankYou" checked={enabledThankYouPage} onChange={(e) => setEnabledThankYouPage(e.target.checked)} />
                <label className="form-check-label" htmlFor="enableThankYou">Show Thank You Message</label>
              </div>

              {enabledThankYouPage && (
                <>
                  <div className="form-floating mt-3">
                    <textarea className="form-control" style={{ height: 90 }} value={thankYouPageMessage} onChange={(e) => setThankYouPageMessage(e.target.value)} placeholder="Your thank you message" />
                    <label>Thank You Message</label>
                  </div>

                  <div className="form-check mt-3">
                    <input className="form-check-input" type="checkbox" id="enableRedirect" checked={enabledRedirect} onChange={(e) => setEnabledRedirect(e.target.checked)} />
                    <label className="form-check-label" htmlFor="enableRedirect">Redirect to URL after submit</label>
                  </div>

                  {enabledRedirect && (
                    <>
                      <div className="form-floating mt-3">
                        <input type="url" className="form-control" value={redirectUrl} onChange={(e) => setRedirectUrl(sanitizeUrl(e.target.value))} placeholder="https://example.com" />
                        <label>Redirect URL</label>
                      </div>

                      <div className="form-check mt-3">
                        <input className="form-check-input" type="checkbox" id="isAutoRedirect" checked={isAutoRedirect} onChange={(e) => setIsAutoRedirect(e.target.checked)} />
                        <label className="form-check-label" htmlFor="isAutoRedirect">Auto Redirect?</label>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="ds-card p-3 mt-3">
              <h6 className="mb-3" style={{ fontWeight: 800, opacity: 0.85 }}>Form Tracking</h6>

              <div className="form-check mb-2">
                <input className="form-check-input" type="checkbox" id="enablePixel" checked={enablePixel} onChange={(e) => setEnablePixel(e.target.checked)} />
                <label className="form-check-label" htmlFor="enablePixel">Enable Tracking Pixels (Meta, TikTok, Google Ads)</label>
              </div>

              {enablePixel && (
                <>
                  <div className="form-floating mt-3">
                    <textarea className="form-control" placeholder="Meta Pixel ID" value={faceBookPixel} onChange={(e) => setFaceBookPixel(e.target.value)} style={{ height: 90 }} />
                    <label>Meta Pixel ID</label>
                  </div>

                  <div className="form-floating mt-3">
                    <textarea className="form-control" placeholder="TikTok Pixel ID" value={tikTokPixel} onChange={(e) => setTikTokPixel(e.target.value)} style={{ height: 90 }} />
                    <label>TikTok Pixel ID</label>
                  </div>

                  <div className="form-floating mt-3">
                    <textarea className="form-control" placeholder="Google Ads Conversion ID" value={googlePixel} onChange={(e) => setGooglePixel(e.target.value)} style={{ height: 90 }} />
                    <label>Google Ads Conversion ID</label>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* RIGHT: Preview */}
          <div className="col-lg-6">
            <div className="ds-card p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="fw-bold" style={{ fontSize: 14 }}>Preview</div>
                  <div className="text-muted" style={{ fontSize: 12, overflowWrap: "anywhere" }}>{previewLink || "-"}</div>
                </div>

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    if (!previewLink) return;
                    navigator.clipboard.writeText(previewLink);
                    toast.success("Link copied!");
                  }}
                >
                  <i className="bi bi-clipboard me-1"></i> Copy
                </button>
              </div>

              <div className="mt-3 text-center">
                {getPreviewUrl() && (
                  <div className="position-relative d-inline-block">
                    <img
                      src={getPreviewUrl()}
                      alt="preview"
                      className="img-fluid rounded-4 mb-3"
                      style={{ maxHeight: 240, objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger rounded-circle position-absolute"
                      style={{ top: 10, right: 10, lineHeight: 1 }}
                      onClick={() => setFormImage(null)}
                      title="Remove Image"
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                )}

                <h4 className="mb-1">{formTitle || "Form Title Preview"}</h4>
                <p className="text-muted">{formDescription || "Form description will appear here."}</p>
              </div>

              <form>
                {selectedFields.map((field, idx) => (
                  <div key={idx} className="card mb-3 border-0 shadow-sm" style={{ background: "#f8f9fa" }}>
                    <div className="card-body py-3 px-2">
                      {field.field === "phone" ? (
                        <>
                          <label className="form-label mb-1" style={{ fontSize: "0.95rem", fontWeight: 600 }}>
                            {field.label}
                          </label>
                          <div className="input-group">
                            <div className="input-group-text p-0" style={{ zIndex: 5 }}>
                              <Select
                                options={countryOptions}
                                styles={{ control: (base) => ({ ...base, border: "none", boxShadow: "none" }) }}
                                className="border-0"
                              />
                            </div>
                            <input type="number" className="form-control shadow-none" placeholder={field.label} />
                          </div>
                        </>
                      ) : field.type === "select" ? (
                        <>
                          <label className="form-label mb-1" style={{ fontSize: "0.95rem", fontWeight: 600 }}>
                            {field.label}
                          </label>
                          <select className="form-select shadow-none" defaultValue="">
                            <option value="" disabled hidden>-- Select Answer --</option>
                            {(field.options || []).map((opt, i) => (
                              <option key={i}>{opt}</option>
                            ))}
                          </select>
                        </>
                      ) : (
                        <>
                          <label className="form-label mb-1" style={{ fontSize: "0.95rem", fontWeight: 600 }}>
                            {field.label}
                          </label>
                          <input type="text" className="form-control shadow-none" />
                        </>
                      )}
                    </div>
                  </div>
                ))}

                <button type="button" className="btn w-100 text-white" style={{ backgroundColor: formTheme }}>
                  Submit Form
                </button>
              </form>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="ss-modal-footer">
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Saving..." : "Save Form"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditLeadFormModal;
